export type Hero3DSkipReason =
  | "query-flag"
  | "small-screen"
  | "save-data"
  | "slow-network"
  | "low-cpu"
  | "low-memory"
  | "no-webgl2"
  | "software-renderer"

export type WebGLSupport = "hardware" | "software" | "none"

type NetworkInformationLike = {
  saveData?: boolean
  effectiveType?: string
}

type NavigatorLike = {
  hardwareConcurrency?: number
  deviceMemory?: number
  connection?: NetworkInformationLike
}

export type Hero3DEnvironment = {
  search: string
  /** CSS pixels (window.innerWidth). */
  viewportWidth: number
  navigator: NavigatorLike
  probeWebGL: () => WebGLSupport
}

/** `?no3d=1` forces the static PNG, handy for QA and for comparing both paths. */
export const NO_3D_QUERY_PARAM = "no3d"

/**
 * Phones and small tablets get the static image: the single-column hero (≤ @screen-md-max) shows the model small, and
 * on a phone its 1.7 MB download plus ~4 s of main-thread work (Lighthouse, mobile) cost more than it adds.
 */
export const MIN_3D_VIEWPORT_WIDTH = 992

const SLOW_CONNECTION_TYPES = ["slow-2g", "2g"]
// CPU rasterisers: WebGL "works" but a continuously animated scene would be slow and drain the battery.
const SOFTWARE_RENDERER = /swiftshader|llvmpipe|softpipe|software|basic render driver/i

export function probeWebGL(): WebGLSupport {
  try {
    const canvas = document.createElement("canvas")
    // failIfMajorPerformanceCaveat rejects most software fallbacks; the renderer-name check catches the rest.
    const gl = canvas.getContext("webgl2", { failIfMajorPerformanceCaveat: true })
    if (!gl) return "none"
    // Firefox deprecates WEBGL_debug_renderer_info but already returns the unmasked name from RENDERER.
    const debugInfo = navigator.userAgent.includes("Firefox") ? null : gl.getExtension("WEBGL_debug_renderer_info")
    const renderer = String(gl.getParameter(debugInfo ? debugInfo.UNMASKED_RENDERER_WEBGL : gl.RENDERER) ?? "")
    gl.getExtension("WEBGL_lose_context")?.loseContext()
    return SOFTWARE_RENDERER.test(renderer) ? "software" : "hardware"
  } catch {
    return "none"
  }
}

function browserEnvironment(): Hero3DEnvironment {
  return {
    search: window.location.search,
    viewportWidth: window.innerWidth,
    navigator: navigator as NavigatorLike,
    probeWebGL,
  }
}

/**
 * Returns why the 3D hero should not run on this device, or `null` when it may.
 * Cheap checks run first so the WebGL probe (which creates a context) is only done when needed.
 */
export function getHero3DSkipReason(env: Hero3DEnvironment = browserEnvironment()): Hero3DSkipReason | null {
  const flag = new URLSearchParams(env.search).get(NO_3D_QUERY_PARAM)
  if (flag !== null && flag !== "0" && flag !== "false") return "query-flag"
  if (env.viewportWidth < MIN_3D_VIEWPORT_WIDTH) return "small-screen"

  const { connection, hardwareConcurrency, deviceMemory } = env.navigator
  if (connection?.saveData) return "save-data"
  if (connection?.effectiveType && SLOW_CONNECTION_TYPES.includes(connection.effectiveType)) return "slow-network"
  if (typeof hardwareConcurrency === "number" && hardwareConcurrency <= 2) return "low-cpu"
  if (typeof deviceMemory === "number" && deviceMemory <= 2) return "low-memory"

  const webgl = env.probeWebGL()
  if (webgl === "none") return "no-webgl2"
  if (webgl === "software") return "software-renderer"

  return null
}
